/**
 * Chat Store — Typed Normalizer Bridges
 *
 * These functions convert Record<string,unknown> outputs from the generic
 * normalizeMessage/normalizeConversations utilities into typed domain objects.
 * They exist to bridge the declared vs actual return types at the API boundary
 * without using `as` type assertions.
 */

import type {
  Conversation,
  ConversationParticipant,
  Message,
  MessageMetadata,
} from './chatStore.types';
import { normalizeMessage } from '@/lib/api-utils';
import { identityFieldsFromApi } from '@/lib/identity';

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function booleanValue(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function rawSender(raw: Record<string, unknown>): Message['sender'] {
  const s = raw.sender instanceof Object ? Object.fromEntries(Object.entries(raw.sender)) : {};
  const identity = identityFieldsFromApi(s);
  const sender: Message['sender'] = {
    id: identity.id,
    username: identity.username,
    displayName: identity.displayName,
    avatarUrl: identity.avatarUrl,
    avatarBorderId: identity.avatarBorderId,
    equippedTitleId: identity.equippedTitleId,
    equippedBadgeIds: identity.equippedBadgeIds,
    equippedNameplateId: identity.equippedNameplateId,
    profileColor: identity.profileColor,
    profileTheme: identity.profileTheme,
    chatTheme: identity.chatTheme,
    displayNameFont: identity.displayNameFont,
    displayNameEffect: identity.displayNameEffect,
    displayNameColor: identity.displayNameColor,
    displayNameSecondaryColor: identity.displayNameSecondaryColor,
  };
  return sender;
}

function rawMessageType(raw: Record<string, unknown>): Message['messageType'] {
  const t = raw.messageType ?? raw.contentType;
  if (
    t === 'image' ||
    t === 'video' ||
    t === 'file' ||
    t === 'audio' ||
    t === 'voice' ||
    t === 'sticker' ||
    t === 'gif' ||
    t === 'system'
  )
    return t;
  return 'text';
}

function rawMetadata(raw: Record<string, unknown>): MessageMetadata {
  if (raw.metadata instanceof Object) {
    const m: MessageMetadata = Object.fromEntries(Object.entries(raw.metadata));
    return m;
  }
  return {};
}

function rawReactionUser(raw: unknown): Message['reactions'][number]['user'] | null {
  if (!(raw instanceof Object)) return null;

  const record = Object.fromEntries(Object.entries(raw));
  const id = stringValue(record.id ?? record.userId ?? record.user_id);
  if (!id) return null;

  return {
    id,
    username:
      stringValue(record.username ?? record.displayName ?? record.display_name) ?? 'Unknown User',
  };
}

function rawReactionUsers(raw: unknown): Array<{ id: string; username: string }> {
  if (!Array.isArray(raw)) return [];

  return raw
    .map(rawReactionUser)
    .filter((user): user is { id: string; username: string } => user !== null);
}

export function normalizeMessageReactions(raw: unknown): Message['reactions'] {
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((value, index): Message['reactions'] => {
    if (!(value instanceof Object)) return [];

    const record = Object.fromEntries(Object.entries(value));
    const emoji = stringValue(record.emoji);
    if (!emoji) return [];

    const users = rawReactionUsers(record.users);
    const user = rawReactionUser(record.user) ?? users[0] ?? null;
    const userId = stringValue(record.userId ?? record.user_id) ?? user?.id ?? `reaction-${index}`;
    const normalizedUser = user ?? { id: userId, username: 'Unknown User' };
    const count = numberValue(record.count);
    const hasReacted = booleanValue(record.hasReacted ?? record.has_reacted);

    return [
      {
        id: stringValue(record.id) ?? `${emoji}-${userId}-${index}`,
        emoji,
        userId,
        user: normalizedUser,
        count,
        users: users.length > 0 ? users : undefined,
        hasReacted,
      },
    ];
  });
}

/**
 * Convert a normalized message Record<string,unknown> to a typed Message.
 * Ensures required string fields never remain null/undefined.
 */
export function toTypedMessage(raw: Record<string, unknown>): Message {
  const message: Message = {
    id: typeof raw.id === 'string' ? raw.id : String(raw.id ?? ''),
    conversationId: typeof raw.conversationId === 'string' ? raw.conversationId : '',
    senderId: typeof raw.senderId === 'string' ? raw.senderId : '',
    content: typeof raw.content === 'string' ? raw.content : '',
    encryptedContent: typeof raw.encryptedContent === 'string' ? raw.encryptedContent : null,
    isEncrypted: raw.isEncrypted === true,
    messageType: rawMessageType(raw),
    replyToId: typeof raw.replyToId === 'string' ? raw.replyToId : null,
    replyTo: null,
    isPinned: raw.isPinned === true,
    isEdited: raw.isEdited === true,
    deletedAt: typeof raw.deletedAt === 'string' ? raw.deletedAt : null,
    metadata: rawMetadata(raw),
    reactions: normalizeMessageReactions(raw.reactions),
    sender: rawSender(raw),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date().toISOString(),
  };

  if ('clientMessageId' in raw || 'client_message_id' in raw) {
    message.clientMessageId =
      typeof raw.clientMessageId === 'string'
        ? raw.clientMessageId
        : typeof raw.client_message_id === 'string'
          ? raw.client_message_id
          : null;
  }

  if ('sequence' in raw) {
    message.sequence = typeof raw.sequence === 'number' ? raw.sequence : null;
  }

  if ('ephemeralPublicKey' in raw) {
    message.ephemeralPublicKey =
      typeof raw.ephemeralPublicKey === 'string' ? raw.ephemeralPublicKey : undefined;
  }

  if ('nonce' in raw) {
    message.nonce = typeof raw.nonce === 'string' ? raw.nonce : undefined;
  }

  if ('senderIdentityKey' in raw) {
    message.senderIdentityKey =
      typeof raw.senderIdentityKey === 'string' ? raw.senderIdentityKey : undefined;
  }

  if ('decryptionFailed' in raw || 'decryptFailed' in raw) {
    message.decryptionFailed =
      raw.decryptionFailed === true || raw.decryptFailed === true ? true : undefined;
  }

  if ('requiresMobile' in raw) {
    message.requiresMobile =
      typeof raw.requiresMobile === 'boolean' ? raw.requiresMobile : undefined;
  }

  if ('scheduledAt' in raw) {
    message.scheduledAt = typeof raw.scheduledAt === 'string' ? raw.scheduledAt : null;
  }

  if ('scheduleStatus' in raw) {
    message.scheduleStatus =
      raw.scheduleStatus === 'scheduled' ||
      raw.scheduleStatus === 'sent' ||
      raw.scheduleStatus === 'cancelled'
        ? raw.scheduleStatus
        : 'immediate';
  }

  if ('forwardedFromId' in raw) {
    message.forwardedFromId = typeof raw.forwardedFromId === 'string' ? raw.forwardedFromId : null;
  }

  if ('forwardedFromUserId' in raw) {
    message.forwardedFromUserId =
      typeof raw.forwardedFromUserId === 'string' ? raw.forwardedFromUserId : null;
  }

  if ('forwardedFromUserName' in raw) {
    message.forwardedFromUserName =
      typeof raw.forwardedFromUserName === 'string' ? raw.forwardedFromUserName : null;
  }

  return message;
}

/**
 * Convenience: normalize a raw API response object to a typed Message in one step.
 */
export function normalizeToMessage(raw: Record<string, unknown>): Message {
  return toTypedMessage(normalizeMessage(raw));
}

/**
 * Converts a normalized conversation payload into a typed Conversation domain object.
 */
export function toTypedConversation(raw: Record<string, unknown>): Conversation {
  const participants: ConversationParticipant[] = Array.isArray(raw.participants)
    ? raw.participants.map((p: unknown): ConversationParticipant => {
        const pr = p instanceof Object ? Object.fromEntries(Object.entries(p)) : {};
        const userRaw =
          pr.user instanceof Object ? Object.fromEntries(Object.entries(pr.user)) : {};
        const identity = identityFieldsFromApi(userRaw);
        return {
          id: typeof pr.id === 'string' ? pr.id : '',
          userId: typeof pr.userId === 'string' ? pr.userId : '',
          nickname: typeof pr.nickname === 'string' ? pr.nickname : null,
          isMuted: pr.isMuted === true,
          mutedUntil: typeof pr.mutedUntil === 'string' ? pr.mutedUntil : null,
          messageRequestStatus:
            pr.messageRequestStatus === 'pending' ||
            pr.messageRequestStatus === 'accepted' ||
            pr.messageRequestStatus === 'rejected' ||
            pr.messageRequestStatus === 'blocked'
              ? pr.messageRequestStatus
              : null,
          joinedAt: typeof pr.joinedAt === 'string' ? pr.joinedAt : '',
          user: {
            id: identity.id,
            username: identity.username,
            displayName: identity.displayName,
            avatarUrl: identity.avatarUrl,
            avatarBorderId: identity.avatarBorderId,
            equippedTitleId: identity.equippedTitleId,
            equippedBadgeIds: identity.equippedBadgeIds,
            equippedNameplateId: identity.equippedNameplateId,
            profileColor: identity.profileColor,
            profileTheme: identity.profileTheme,
            chatTheme: identity.chatTheme,
            displayNameFont: identity.displayNameFont,
            displayNameEffect: identity.displayNameEffect,
            displayNameColor: identity.displayNameColor,
            displayNameSecondaryColor: identity.displayNameSecondaryColor,
            status: identity.status,
          },
        };
      })
    : [];
  const conversationType =
    raw.conversationType === 'secret' || raw.conversationType === 'cloud'
      ? raw.conversationType
      : undefined;
  return {
    id: typeof raw.id === 'string' ? raw.id : String(raw.id ?? ''),
    type: raw.type === 'group' ? 'group' : 'direct',
    conversationType,
    name: typeof raw.name === 'string' ? raw.name : null,
    avatarUrl: typeof raw.avatarUrl === 'string' ? raw.avatarUrl : null,
    participants,
    lastMessage:
      raw.lastMessage instanceof Object
        ? toTypedMessage(Object.fromEntries(Object.entries(raw.lastMessage)))
        : null,
    unreadCount: typeof raw.unreadCount === 'number' ? raw.unreadCount : 0,
    isMuted: raw.isMuted === true,
    mutedUntil: typeof raw.mutedUntil === 'string' ? raw.mutedUntil : null,
    isArchived: raw.isArchived === true,
    isPinned: raw.isPinned === true,
    isNoteToSelf: raw.isNoteToSelf === true,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date().toISOString(),
  };
}
